import { useState, useEffect } from "react";
import axios from "axios";
import { BiSearch } from "react-icons/bi";
import PageBanner from "./PageBanner";
import SocialMediaSidebar from "./SocialMediaSidebar";
import SectionHeader from "./SectionHeader";
import { EcomAPI,CMSAPI } from "../config";
import ScrollToTopButton from "./ScrollToTopButton";

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

const OrderSummary = () => {
  const userId = getCookie("userId") || localStorage.getItem("userId");
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!userId) {
        setError("Please log in to view your orders");
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${CMSAPI}/api/Website/get-Order-by-user-id?userId=${userId}`
        );

        if (!Array.isArray(response.data) || response.data.length === 0) {
          setError("No orders found");
        } else {
          // Add full image URL to each order
          const ordersWithImages = response.data.map((order) => ({
            ...order,
            imageUrl: order.mediaPath ? `${CMSAPI}${order.mediaPath}` : null,
          }));
          setOrders(ordersWithImages);
          setFilteredOrders(ordersWithImages);
        }
      } catch (err) {
        console.error("Fetch orders error:", err);
        setError("Failed to load orders");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredOrders(orders);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = orders.filter((order) => {
      const name = order.name ? order.name.toLowerCase() : "";
      const status = order.orderStatus ? order.orderStatus.toLowerCase() : "";
      return name.includes(query) || status.includes(query);
    });

    setFilteredOrders(filtered);
  }, [searchQuery, orders]);

  const renderOrderCard = (order) => (
    <div key={order.orderId} className="bg-white rounded-lg shadow-md p-4 mb-4">
      <div className="flex gap-4">
        {/* Item Image */}
        <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
          {order.imageUrl ? (
            <img
              src={order.imageUrl}
              alt={order.name || "Order item"}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/100?text=No+Image";
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="flex-1">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-semibold">
              {order.name || `Order #${order.orderId}`}
            </h3>
            <span
              className={`px-2 py-1 rounded text-xs ${
                order.orderStatus === "Completed"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {order.orderStatus || "Processing"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <p className="font-medium">Price</p>
              <p>₹{order.price || "0.00"}</p>
            </div>
            <div>
              <p className="font-medium">Quantity</p>
              <p>{order.quantity || 1}</p>
            </div>
            <div>
              <p className="font-medium">Product ID</p>
              <p>{order.productId || "N/A"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Order Summary"
        breadcrumb={[{ label: "Home", href: "/" }, { label: "Order Summary" }]}
      />

      <div className="max-w-6xl mx-auto p-4">
        <SectionHeader
          title="Order Summary @ AJEEVI"
          shareUrl="https://your-site.com/data-driven-insights"
        />

        <div>
          <ScrollToTopButton />
        </div>

        <div className="container-fluid py-5">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-xl font-bold">My Orders</h4>
                  <div className="relative w-64">
                    <input
                      type="text"
                      className="w-full pl-4 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D084]"
                      placeholder="Search your orders"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <BiSearch className="absolute right-3 top-3 text-gray-400" />
                  </div>
                </div>

                {/* Orders Container */}
                <div id="ordersContainer" className="space-y-4">
                  {isLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00D084] mb-2"></div>
                      <p>Loading your orders...</p>
                    </div>
                  ) : error ? (
                    <div className="text-center py-8">
                      <svg
                        className="w-12 h-12 mx-auto text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <p className="text-red-500 mt-2">{error}</p>
                    </div>
                  ) : filteredOrders.length === 0 ? (
                    <div className="text-center py-8">
                      <svg
                        className="w-12 h-12 mx-auto text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <p className="mt-2">
                        {searchQuery
                          ? "No orders match your search."
                          : "No orders found."}
                      </p>
                    </div>
                  ) : (
                    filteredOrders.map(renderOrderCard)
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSummary;
